// App Variants - Modular Configuration System
import { AppVariant, AppVariantConfig } from '@/types/app-config';

// Import all variant configurations
import { organizationsVariant } from './organizations.variant';
import { crmsVariant } from './crms.variant';
import { churchesVariant } from './churches.variant';
import { teamsVariant } from './teams.variant';
import { marketplacesVariant } from './marketplaces.variant';
import { marketplaceOnlyVariant } from './marketplace-only.variant';
import { customVariant } from './custom.variant';

// Export all individual variants
export {
  organizationsVariant,
  crmsVariant,
  churchesVariant,
  teamsVariant,
  marketplacesVariant,
  marketplaceOnlyVariant,
  customVariant
};

// Export all helper functions
export {
  determineAppVariant,
  getEnabledAppModules,
  getEnabledAdminModules,
  buildCustomVariant,
  validateVariantConfig,
  mergeVariantConfigs
} from './helpers';

// Export validation utilities
export {
  validateVariantWithFeatures,
  validateFeatureNaming,
  debugVariantFeatureConsistency,
  KNOWN_FEATURE_IDS
} from './validation';

// Main variants registry - for backward compatibility and easy access
export const APP_VARIANTS: Record<AppVariant, AppVariantConfig> = {
  organizations: organizationsVariant,
  crms: crmsVariant,
  churches: churchesVariant,
  teams: teamsVariant,
  marketplaces: marketplacesVariant,
  'marketplace-only': marketplaceOnlyVariant,
  custom: customVariant
};

/**
 * Gets a variant configuration by ID
 */
export function getVariant(variantId: AppVariant): AppVariantConfig {
  const variant = APP_VARIANTS[variantId];
  if (!variant) {
    throw new Error(`Unknown app variant: ${variantId}`);
  }
  return variant;
}

/**
 * Gets all available variant IDs
 */
export function getAvailableVariants(): AppVariant[] {
  return Object.keys(APP_VARIANTS) as AppVariant[];
}

/**
 * Gets all available variants with metadata
 */
export function getVariantOptions(): Array<{ id: AppVariant; name: string; description: string }> {
  return Object.values(APP_VARIANTS).map(variant => ({
    id: variant.id,
    name: variant.name,
    description: variant.description
  }));
} 